// Client-side mirror of the backend name rules (app/core/filenames.py), so the
// UI can say what is wrong inline instead of round-tripping. The backend
// answers the same problems as `invalid_filename` / `invalid_folder_name`
// with `params.reason`.
//
// Files: unicode letters/digits/underscore, space, dot, parens, hyphen; at
// most 200 characters; not a name a desktop can't save (dots only, CON,
// NUL.txt, COM1…).
// Folders: display names other modules make too ("A-1 — Client, LLC"), so
// any text on one line, at most 255 characters, not dots only.

export const MAX_FILENAME_LENGTH = 200;
export const MAX_FOLDER_NAME_LENGTH = 255;

// \p{L}/\p{N} need the /u flag; \w in JS is ASCII-only, so spell it out.
const FILENAME_RE = /^[\p{L}\p{N}_ .()-]+$/u;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const RESERVED_RE = /^(\.+|(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?)$/i;

/** Why a file name is refused: 'empty' | 'too_long' | 'invalid_chars' | 'reserved' | null. */
export function filenameProblem(name) {
  const cleaned = (name || '').trim();
  if (!cleaned) return 'empty';
  if (cleaned.length > MAX_FILENAME_LENGTH) return 'too_long';
  if (!FILENAME_RE.test(cleaned)) return 'invalid_chars';
  if (RESERVED_RE.test(cleaned)) return 'reserved';
  return null;
}

export function isValidFilename(name) {
  return filenameProblem(name) === null;
}

/** Why a folder name is refused: 'empty' | 'too_long' | 'invalid_chars' | 'reserved' | null. */
export function folderNameProblem(name) {
  const cleaned = (name || '').trim();
  if (!cleaned) return 'empty';
  if (cleaned.length > MAX_FOLDER_NAME_LENGTH) return 'too_long';
  if (CONTROL_RE.test(cleaned)) return 'invalid_chars';
  if (RESERVED_RE.test(cleaned)) return 'reserved';
  return null;
}

/**
 * The sentence for a name problem (`my-docs:names.*`), for a file or a
 * folder. Also reads the backend's refusal: `{reason}` from
 * `invalid_filename` / `invalid_folder_name` params.
 */
export function nameProblemMessage(t, problem, kind = 'file') {
  if (!problem) return null;
  const max = kind === 'folder' ? MAX_FOLDER_NAME_LENGTH : MAX_FILENAME_LENGTH;
  const key = problem === 'invalid_chars' ? `invalidChars_${kind}` : problem === 'too_long' ? 'tooLong' : problem;
  return t(`my-docs:names.${key}`, { max });
}
